import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Settings, Phone, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useFM } from '@/context/FMContext';
import { axiosInstance } from '@/App';
import { formatCurrency } from '@/lib/fmUtils';
import TrainerCard from '@/components/fm/TrainerCard';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';

/**
 * FMTrainerScorecard - Displays ranked trainer performance cards for the selected gym.
 * Features drill-down Drawer and trainer management (add/edit).
 */
export default function FMTrainerScorecard() {
  const { selectedGymId, selectedGym } = useFM();
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthLabel, setMonthLabel] = useState('');

  // Drill-down drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);

  // Add trainer dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone: '', rank: '1' });
  const [adding, setAdding] = useState(false);

  // Manage trainers sheet state
  const [manageOpen, setManageOpen] = useState(false);
  const [allTrainers, setAllTrainers] = useState([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);

  const fetchScorecards = useCallback(async () => {
    if (!selectedGymId) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/fm/trainers/${selectedGymId}/scorecards`);
      setScorecards(res.data.trainers || []);
      if (res.data.month) {
        const [y, m] = res.data.month.split('-');
        const date = new Date(Number(y), Number(m) - 1);
        setMonthLabel(date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }));
      }
    } catch (err) {
      toast.error('Failed to load scorecards');
    } finally {
      setLoading(false);
    }
  }, [selectedGymId]);

  useEffect(() => {
    fetchScorecards();
  }, [fetchScorecards]);

  // Pull-to-refresh handler
  const handleRefresh = async () => {
    await fetchScorecards();
  };

  // Drill-down: open Drawer with selected trainer
  const handleTrainerTap = (trainer) => {
    setSelectedTrainer(trainer);
    setDrawerOpen(true);
  };

  // Add trainer form submission
  const handleAddTrainer = async () => {
    if (!addForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setAdding(true);
    try {
      await axiosInstance.post('/fm/trainers', {
        name: addForm.name.trim(),
        phone: addForm.phone.trim() || null,
        rank: Number(addForm.rank),
        gym_id: selectedGymId,
      });
      toast.success(`Trainer ${addForm.name} added`);
      setAddForm({ name: '', phone: '', rank: '1' });
      setAddDialogOpen(false);
      fetchScorecards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add trainer');
    } finally {
      setAdding(false);
    }
  };

  // Fetch all trainers for manage view
  const fetchAllTrainers = useCallback(async () => {
    if (!selectedGymId) return;
    setLoadingTrainers(true);
    try {
      const res = await axiosInstance.get('/fm/trainers', { params: { gym_id: selectedGymId } });
      setAllTrainers(res.data.trainers || []);
    } catch {
      toast.error('Failed to load trainers');
    } finally {
      setLoadingTrainers(false);
    }
  }, [selectedGymId]);

  const handleOpenManage = () => {
    fetchAllTrainers();
    setManageOpen(true);
  };

  // Summary stats
  const avgScore = scorecards.length > 0
    ? Math.round(scorecards.reduce((sum, t) => sum + t.overall_score, 0) / scorecards.length)
    : 0;
  const topTrainer = scorecards.length > 0 ? scorecards[0]?.trainer_name : '--';

  if (!selectedGymId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="w-10 h-10 text-slate-300 mb-3" />
        <p className="text-sm text-slate-500">Select a gym to view trainer scorecards</p>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={handleRefresh} pullingContent="" refreshingContent={<Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />}>
      <div className="space-y-4 pb-20">
        {/* Page Title */}
        <div>
          <h2 className="text-lg font-bold text-slate-800">
            Trainer Scorecards
          </h2>
          {monthLabel && (
            <p className="text-sm text-slate-500 mt-0.5">{monthLabel}</p>
          )}
        </div>

        {/* Summary Bar */}
        {!loading && scorecards.length > 0 && (
          <div className="rounded-lg bg-slate-50 border p-3 flex items-center justify-between text-xs text-slate-600">
            <span>{scorecards.length} trainer{scorecards.length !== 1 ? 's' : ''}</span>
            <span className="text-slate-300">|</span>
            <span>Avg Score: <span className="font-semibold text-slate-800">{avgScore}%</span></span>
            <span className="text-slate-300">|</span>
            <span className="truncate max-w-[100px]">Top: <span className="font-semibold text-slate-800">{topTrainer}</span></span>
          </div>
        )}

        {/* Loading Skeleton */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border p-5 bg-white shadow-sm animate-pulse">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-5 bg-slate-200 rounded-md" />
                    <div className="w-28 h-5 bg-slate-200 rounded" />
                  </div>
                  <div className="w-12 h-6 bg-slate-200 rounded" />
                </div>
                <div className="h-2 bg-slate-200 rounded-full mb-4" />
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5, 6].map((j) => (
                    <div key={j} className="flex items-center gap-3">
                      <div className="w-[110px] h-3 bg-slate-100 rounded" />
                      <div className="w-[36px] h-3 bg-slate-100 rounded" />
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && scorecards.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Users className="w-10 h-10 text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 mb-1">No trainers found</p>
            <p className="text-xs text-slate-400">Add trainers to see their scorecards</p>
          </div>
        )}

        {/* Trainer Cards - sorted by overall_score descending */}
        {!loading && scorecards.length > 0 && (
          <div className="space-y-4">
            {scorecards.map((trainer, idx) => (
              <TrainerCard
                key={trainer.trainer_id}
                trainer={trainer}
                rank={idx + 1}
                onClick={() => handleTrainerTap(trainer)}
              />
            ))}
          </div>
        )}

        {/* Add Trainer + Manage Row */}
        {!loading && (
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={() => setAddDialogOpen(true)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 active:scale-[0.98] transition-all min-h-[48px]"
            >
              <Plus className="w-4 h-4" />
              Add Trainer
            </button>
            <button
              onClick={handleOpenManage}
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium text-slate-600 hover:bg-slate-50 active:scale-[0.98] transition-all min-h-[48px]"
            >
              <Settings className="w-4 h-4" />
              Manage
            </button>
          </div>
        )}

        {/* ---- Drill-Down Drawer ---- */}
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerContent className="max-h-[85vh]">
            <DrawerHeader>
              <DrawerTitle>
                {selectedTrainer?.trainer_name || 'Trainer'} - Detailed Scorecard
              </DrawerTitle>
              <DrawerDescription>
                Performance breakdown for {monthLabel || 'this month'}
              </DrawerDescription>
            </DrawerHeader>

            {selectedTrainer && (
              <div className="px-4 pb-6 space-y-5 overflow-y-auto">
                {/* Overall Score */}
                <div className="text-center">
                  <span className="text-4xl font-bold text-slate-800">
                    {Math.round(selectedTrainer.overall_score)}%
                  </span>
                  <p className="text-xs text-slate-500 mt-1">Overall Performance Score</p>
                </div>

                {/* Revenue Section */}
                <div className="rounded-lg border p-4 bg-slate-50">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">PT Revenue</h4>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-600">Achieved</span>
                    <span className="font-bold text-slate-800">{formatCurrency(selectedTrainer.trainer_revenue)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-600">Target</span>
                    <span className="text-slate-600">{formatCurrency(selectedTrainer.trainer_target)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className={`h-2 rounded-full transition-all ${selectedTrainer.pt_achievement_pct >= 90 ? 'bg-green-500' : selectedTrainer.pt_achievement_pct >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(selectedTrainer.pt_achievement_pct, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1 text-right">{Math.round(selectedTrainer.pt_achievement_pct)}% achievement</p>
                </div>

                {/* Compliance Section */}
                <div className="rounded-lg border p-4 bg-slate-50">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Compliance Rates</h4>
                  <div className="space-y-3">
                    {[
                      { label: '7-Day Profile', value: selectedTrainer.compliance_7day_pct },
                      { label: '14-Day BMI', value: selectedTrainer.compliance_14day_pct },
                      { label: '30-Day BCA', value: selectedTrainer.compliance_30day_pct },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                          <span>{item.label}</span>
                          <span className="font-semibold">{Math.round(item.value)}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                          <div
                            className={`h-2 rounded-full transition-all ${item.value >= 90 ? 'bg-green-500' : item.value >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(item.value, 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Discipline & Referrals */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border p-4 bg-slate-50 text-center">
                    <span className="text-2xl font-bold text-slate-800">{Math.round(selectedTrainer.discipline_pct)}%</span>
                    <p className="text-xs text-slate-500 mt-1">Discipline</p>
                  </div>
                  <div className="rounded-lg border p-4 bg-slate-50 text-center">
                    <span className="text-2xl font-bold text-slate-800">{selectedTrainer.referrals_count}</span>
                    <p className="text-xs text-slate-500 mt-1">Referrals</p>
                  </div>
                </div>

                {/* Strengths / Areas for Improvement */}
                <div className="rounded-lg border p-4 bg-white">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Summary</h4>
                  <div className="space-y-1.5 text-xs text-slate-600">
                    {selectedTrainer.pt_achievement_pct >= 80 && (
                      <p className="text-green-700">+ Strong PT revenue achievement</p>
                    )}
                    {selectedTrainer.compliance_7day_pct >= 95 && selectedTrainer.compliance_14day_pct >= 95 && (
                      <p className="text-green-700">+ Excellent compliance rates</p>
                    )}
                    {selectedTrainer.discipline_pct >= 95 && (
                      <p className="text-green-700">+ High discipline score</p>
                    )}
                    {selectedTrainer.referrals_count >= 3 && (
                      <p className="text-green-700">+ Good referral generation</p>
                    )}
                    {selectedTrainer.pt_achievement_pct < 50 && (
                      <p className="text-red-600">- PT revenue below 50% of target</p>
                    )}
                    {selectedTrainer.compliance_7day_pct < 80 && (
                      <p className="text-red-600">- 7-Day compliance needs attention</p>
                    )}
                    {selectedTrainer.compliance_14day_pct < 80 && (
                      <p className="text-red-600">- 14-Day BMI compliance low</p>
                    )}
                    {selectedTrainer.compliance_30day_pct < 80 && (
                      <p className="text-red-600">- 30-Day BCA compliance low</p>
                    )}
                    {selectedTrainer.referrals_count < 1 && (
                      <p className="text-yellow-700">- No referrals this month</p>
                    )}
                    {selectedTrainer.overall_score >= 85 && (
                      <p className="mt-2 text-sm font-medium text-green-700">Overall: High performer</p>
                    )}
                    {selectedTrainer.overall_score >= 60 && selectedTrainer.overall_score < 85 && (
                      <p className="mt-2 text-sm font-medium text-yellow-700">Overall: Needs coaching in weak areas</p>
                    )}
                    {selectedTrainer.overall_score < 60 && (
                      <p className="mt-2 text-sm font-medium text-red-700">Overall: Requires immediate attention</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DrawerContent>
        </Drawer>

        {/* ---- Add Trainer Dialog ---- */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-sm mx-4 rounded-xl">
            <DialogHeader>
              <DialogTitle>Add Trainer</DialogTitle>
              <DialogDescription>
                Add a new trainer to {selectedGym?.name || selectedGym?.gym_name || 'this gym'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Name *</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Trainer name"
                  className="w-full h-10 px-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Phone (optional)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="tel"
                    value={addForm.phone}
                    onChange={(e) => setAddForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone number"
                    className="w-full h-10 pl-9 pr-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Performance Rank</label>
                <select
                  value={addForm.rank}
                  onChange={(e) => setAddForm(prev => ({ ...prev, rank: e.target.value }))}
                  className="w-full h-10 px-3 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="1">Rank 1 (30% weightage)</option>
                  <option value="2">Rank 2 (27% weightage)</option>
                  <option value="3">Rank 3 (23% weightage)</option>
                  <option value="4">Rank 4 (20% weightage)</option>
                </select>
              </div>
              <button
                onClick={handleAddTrainer}
                disabled={adding || !addForm.name.trim()}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2.5 rounded-md font-medium disabled:opacity-50 min-h-[44px] transition-colors flex items-center justify-center gap-2"
              >
                {adding && <Loader2 className="w-4 h-4 animate-spin" />}
                {adding ? 'Adding...' : 'Add Trainer'}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ---- Manage Trainers Sheet ---- */}
        <Sheet open={manageOpen} onOpenChange={setManageOpen}>
          <SheetContent side="bottom" className="rounded-t-xl max-h-[80vh]">
            <SheetHeader>
              <SheetTitle>Manage Trainers</SheetTitle>
              <SheetDescription>
                {selectedGym?.name || selectedGym?.gym_name || 'Gym'} - All trainers
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 overflow-y-auto max-h-[60vh]">
              {loadingTrainers ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : allTrainers.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No trainers found</p>
              ) : (
                <div className="space-y-3">
                  {allTrainers.map((t) => (
                    <div
                      key={t.trainer_id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-white"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{t.name}</p>
                        <p className="text-xs text-slate-500">
                          Rank {t.rank} | {t.status}
                          {t.phone ? ` | ${t.phone}` : ''}
                        </p>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          t.status === 'Active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {t.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </PullToRefresh>
  );
}
